import { motion, MotionProps } from 'framer-motion'
import { atom, useSetAtom, useAtomValue } from 'jotai'
import { isFileAccepted } from '@shared/file-grants/uploadable-files'
import { processFile } from '../../../utils/fileUtils'
import { MAX_NUMBER_OF_ATTACHMENTS, useAttachments, useAttachmentStore, useRemainingAttachments } from '../../../store'
import { memo, useCallback, useMemo } from 'react'
import { cn, logAmplitudeEvent } from '../../../utils'
import { PaperclipIcon } from '../../icons'
import { useAddChatToast } from '../../../store/toasts'
import { setOverlayInteractive } from '@highlight/ui'
import { FileText, Code, ImageIcon } from 'lucide-react'

const dragAndDropAtom = atom(false)
export const useIsDragAndDropping = () => {
  return useAtomValue(dragAndDropAtom)
}
export const useSetDragAndDropping = () => {
  return useSetAtom(dragAndDropAtom)
}

export const useDragAndDrop = (isExpanded?: boolean, onAttached?: () => void) => {
  const { addFileAttachment } = useAttachmentStore()
  const setDragAndDropping = useSetDragAndDropping()
  const remainingAttachments = useRemainingAttachments()
  const addChatToast = useAddChatToast()

  // Prevent default to allow drop
  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragAndDropping(true)
    },
    [setDragAndDropping],
  )

  const onDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const relatedTarget = e.relatedTarget as HTMLElement
      if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
        return
      }
      e.preventDefault()
      setDragAndDropping(false)

      // Signal overlay to become non-interactive again on drag leave
      setOverlayInteractive(false)
    },
    [setDragAndDropping],
  )

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragAndDropping(false)
      if (remainingAttachments === 0) {
        addChatToast({
          message: 'Maximum attachments reached!',
          type: 'error',
        })
        logAmplitudeEvent('Attachment Limit Wall')
        return
      }

      const files = Array.from(e.dataTransfer.files).slice(0, remainingAttachments)
      let added = []
      for (const file of files) {
        if (file && isFileAccepted(file)) {
          const attach = await processFile(file)
          if (attach.error) {
            addChatToast({ message: attach.error, type: 'error' })
            continue
          }
          addFileAttachment(attach.file, attach.value)
          added.push(attach)
        }
      }
      if (added.length > 0) {
        onAttached?.()
        logAmplitudeEvent('Drag & Drop Attachments', {
          amount: added.length,
          types: added.map((attach) => attach.file.type),
        })
      }
    },
    [setDragAndDropping, addFileAttachment, remainingAttachments, isExpanded],
  )

  return useMemo(() => {
    return {
      onDragOver,
      onDragLeave,
      onDrop,
    }
  }, [onDragOver, onDragLeave, onDrop])
}

export const DragAndDrop = memo(({ isExpanded }: { isExpanded?: boolean }) => {
  const isDragAndDropping = useIsDragAndDropping()
  if (!isDragAndDropping) {
    return null
  }
  return (
    <motion.div
      {...fadeIn}
      className={cn(
        'pointer-events-none absolute left-0 top-0 z-[2] flex h-full w-full items-center gap-1',
        isExpanded !== false ? 'bg-secondary-old/90 flex-col justify-center' : 'bg-secondary-old/95 gap-3 px-1.5',
      )}
    >
      {isExpanded !== false ? (
        <>
          <div className="text-light relative h-16 w-16 opacity-60">
            {/* ImageIcon appears second */}
            <motion.div {...icon1} className={'absolute'}>
              <ImageIcon size={32} />
            </motion.div>

            {/* Code appears third */}
            <motion.div {...icon2} className={'absolute'}>
              <Code size={32} />
            </motion.div>

            {/* FileText appears first */}
            <motion.div {...icon3} className={'absolute'}>
              <FileText size={32} />
            </motion.div>
          </div>
          <span className={'mt-2 text-lg font-medium'}>Add files</span>
          <span className={'text-md'}>Drop any files here to attach them</span>
        </>
      ) : (
        <>
          <div className={'bg-light-5 text-light-80 flex h-8 w-8 items-center justify-center rounded-full'}>
            <PaperclipIcon size={20} />
          </div>
          <span className={'text-light-80 text-sm font-medium leading-none'}>Attach files</span>
        </>
      )}
    </motion.div>
  )
})
DragAndDrop.displayName = 'DragAndDrop'

const fadeIn = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
  transition: { duration: 0.1 },
}

const icon1: MotionProps = {
  initial: {
    opacity: 0,
    scale: 0,
    y: '50%',
    rotate: '-12deg',
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: '50%',
    rotate: '-12deg',
  },
  transition: {
    type: 'spring',
    stiffness: 500,
    damping: 20,
    delay: 0.2,
  },
}

const icon2: MotionProps = {
  initial: {
    opacity: 0,
    scale: 0,
    x: 0,
    y: '50%',
    rotate: '12deg',
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: '50%',
    x: '100%',
    rotate: '12deg',
  },
  transition: {
    type: 'spring',
    stiffness: 500,
    damping: 20,
    delay: 0.3,
  },
}

const icon3: MotionProps = {
  initial: {
    opacity: 0,
    scale: 0,
    y: '100%',
    x: '50%',
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: '100%',
    x: '50%',
  },
  transition: {
    type: 'spring',
    stiffness: 500,
    damping: 20,
    delay: 0.1,
  },
}
